import { skipToken, useQuery, useMutation } from '@tanstack/react-query';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type {
  ShipAndDebitEntityConfig,
  SubmissionRule,
} from '@erp/shared-types';

function useInvalidateEntityModuleConfig() {
  return useInvalidateRpcQueryFn((o) => [
    o.core.entityModuleConfig.get.key(),
    o.core.entityModuleConfig.list.key(),
  ]);
}

export function useEntityModuleConfig(
  entityType: string,
  entityId: string | undefined,
  module: string,
  enabled = true,
) {
  return useQuery({
    ...orpc.core.entityModuleConfig.get.queryOptions({
      input: enabled && entityId ? { entityType, entityId, module } : skipToken,
    }),
  });
}

export function useListEntityModuleConfigs(
  module: string,
  entityType: string,
  enabled = true,
) {
  return useQuery({
    ...orpc.core.entityModuleConfig.list.queryOptions({
      input: enabled ? { module, entityType } : skipToken,
    }),
  });
}

export function useBulkUpdateSubmissionRules() {
  const { showToast } = useToast();
  const invalidate = useInvalidateEntityModuleConfig();

  return useMutation({
    mutationFn: (input: {
      rules: Array<{ rule: SubmissionRule; entityIds: string[] }>;
    }) => rpcClient.core.entityModuleConfig.bulkUpdateSubmissionRules(input),
    onSuccess: ({ updated }) => {
      showToast({
        title: `Submission rules saved (${updated} supplier${updated === 1 ? '' : 's'} updated)`,
        color: 'success',
      });
      invalidate();
    },
    onError: () => {
      showToast({
        title: 'Failed to save submission rules',
        color: 'error',
      });
    },
  });
}

export function useUpdateEntityModuleConfig() {
  const { showToast } = useToast();
  const invalidate = useInvalidateEntityModuleConfig();

  return useMutation({
    mutationFn: (input: {
      entityType: string;
      entityId: string;
      module: string;
      config: ShipAndDebitEntityConfig;
    }) => rpcClient.core.entityModuleConfig.update(input),
    onSuccess: () => {
      showToast({ title: 'Module configuration saved', color: 'success' });
      invalidate();
    },
    onError: () => {
      showToast({
        title: 'Failed to save module configuration',
        color: 'error',
      });
    },
  });
}
